import { SearchFilterV4 } from "@/types/SearchFilter";
import { useCallback, useState } from "react";

import { trackFilterChange } from "./tracking";

export const FILTER_BAR_KEYS = [
  "insight_types",
  "channel_type",
  "channel_name",
  "sentiment",
  "language",
  "mention_kind",
  "reach",
  "freetext",
] as const;

export type FilterBarKey = (typeof FILTER_BAR_KEYS)[number];

type FilterChangeHandler = (value: SearchFilterV4, clear?: boolean) => void;

function isFilterActive(key: FilterBarKey, filters: Partial<SearchFilterV4>) {
  if (key === "reach") {
    return filters.min_reach_threshold !== undefined;
  }

  return Boolean(filters[key]);
}

export function getActiveFilterKeys(filters: Partial<SearchFilterV4>) {
  return FILTER_BAR_KEYS.filter((key) => isFilterActive(key, filters));
}

function reconcileFilterOrder(current: FilterBarKey[], active: FilterBarKey[]) {
  return [
    ...current.filter((key) => active.includes(key)),
    ...active.filter((key) => !current.includes(key)),
  ];
}

function areOrdersEqual(previous: FilterBarKey[], next: FilterBarKey[]) {
  return (
    previous.length === next.length &&
    previous.every((key, index) => key === next[index])
  );
}

export function useFilterOrder(
  filters: Partial<SearchFilterV4>,
  onChange: FilterChangeHandler,
  searchStartDate?: string
) {
  const [filterOrder, setFilterOrder] = useState<FilterBarKey[]>(() =>
    getActiveFilterKeys(filters)
  );

  // Side-effects
  const [prevFilters, setPrevFilters] = useState(filters);
  if (filters !== prevFilters) {
    setPrevFilters(filters);
    const activeFilters = getActiveFilterKeys(filters);
    const nextOrder = reconcileFilterOrder(filterOrder, activeFilters);

    if (!areOrdersEqual(filterOrder, nextOrder)) {
      setFilterOrder(nextOrder);
    }
  }

  const handleChange = useCallback<FilterChangeHandler>(
    (patch, clear = false) => {
      const nextFilters = clear ? patch : { ...filters, ...patch };
      const nextActiveFilters = getActiveFilterKeys(nextFilters);

      setFilterOrder((current) => {
        const nextOrder = reconcileFilterOrder(current, nextActiveFilters);
        return areOrdersEqual(current, nextOrder) ? current : nextOrder;
      });

      if (!clear) {
        trackFilterChange(patch, filters, searchStartDate);
      }

      onChange(patch, clear);
    },
    [filters, onChange, searchStartDate]
  );

  return [filterOrder, handleChange] as const;
}
